import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Star, Plus, Copy, X, ExternalLink, BadgeCheck, MessageSquarePlus, Reply, Code, Flag } from "lucide-react";

function Stars({ value, size = "w-5 h-5" }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${size} ${i <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      ))}
    </div>
  );
}

export default function Reviews() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [invites, setInvites] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [lastLink, setLastLink] = useState("");
  const [replyDrafts, setReplyDrafts] = useState({});

  const load = () => {
    api.get("/reviews").then((r) => setData(r.data)).catch(() => {});
    api.get("/review-invites").then((r) => setInvites(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const linkFor = (token) => `${window.location.origin}/avis/${token}`;
  const copy = (token) => { navigator.clipboard?.writeText(linkFor(token)); toast.success("Lien copié"); };

  const createInvite = async () => {
    try {
      const { data: inv } = await api.post("/review-invites", {
        client_id: clientId || null, client_name: clientName, client_email: clientEmail,
        origin_url: window.location.origin,
      });
      setLastLink(linkFor(inv.token));
      navigator.clipboard?.writeText(linkFor(inv.token));
      toast.success(inv.email_sent ? "Invitation envoyée par email !" : "Lien d'avis créé et copié");
      setShowForm(false); setClientId(""); setClientName(""); setClientEmail("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la création du lien");
    }
  };

  const sendReply = async (id) => {
    const reply = (replyDrafts[id] || "").trim();
    if (!reply) return;
    try {
      await api.post(`/reviews/${id}/reply`, { reply });
      toast.success("Réponse publiée");
      setReplyDrafts((d) => { const n = { ...d }; delete n[id]; return n; });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la publication");
    }
  };

  const reportReview = async (id) => {
    const reason = window.prompt("Pourquoi signaler cet avis ? (facultatif)") ?? null;
    if (reason === null) return;
    try {
      await api.post(`/reviews/${id}/report`, { reason });
      toast.success("Avis signalé — un administrateur va l'examiner");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const agg = data?.aggregate;
  const publicUrl = `${window.location.origin}/annuaire/${user?.company_id}`;
  const widgetEmbed = `<iframe src="${window.location.origin}/widget/avis/${user?.company_id}" width="380" height="320" style="border:0" title="Avis clients"></iframe>`;

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center"><Star className="w-5 h-5 text-sky-400" /></div>
          <div>
            <h1 className="text-3xl font-display font-bold text-navy">Avis clients</h1>
            <p className="text-slate-500">Collectez des avis vérifiés et boostez votre fiche annuaire</p>
          </div>
        </div>
        <div className="flex gap-2">
          <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="view-public-profile"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:border-sky-300 transition-colors">
            <ExternalLink className="w-4 h-4" /> Ma fiche publique
          </a>
          <button onClick={() => { setShowForm(true); setLastLink(""); }} data-testid="new-invite-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-400 text-navy font-semibold hover:bg-sky-500 transition-colors">
            <MessageSquarePlus className="w-4 h-4" /> Inviter un client
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="reviews-aggregate">
        <div className="flex items-center gap-4">
          <div className="text-4xl font-display font-bold text-navy">{agg?.global || "—"}</div>
          <div><Stars value={agg?.global || 0} /><div className="text-sm text-slate-500 mt-1">{agg?.count || 0} avis vérifiés</div></div>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          {(data?.criteria || []).map((cr) => (
            <div key={cr.key}>
              <div className="flex items-center justify-between text-sm mb-1"><span className="text-slate-600">{cr.label}</span><span className="font-semibold text-navy">{agg?.criteria?.[cr.key] || 0} / 5</span></div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-sky-400 rounded-full" style={{ width: `${(agg?.criteria?.[cr.key] || 0) / 5 * 100}%` }} /></div>
            </div>
          ))}
        </div>
      </div>

      {/* Widget embed */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="widget-embed-panel">
        <h3 className="font-display font-semibold text-navy text-lg inline-flex items-center gap-2"><Code className="w-5 h-5 text-sky-500" /> Widget « Avis » pour votre site</h3>
        <p className="text-sm text-slate-500 mt-1">Intégrez ce carrousel d'avis sur votre propre site web.</p>
        <div className="mt-3 flex items-start gap-2">
          <textarea readOnly value={widgetEmbed} data-testid="widget-embed-code" rows={2}
            className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-xs font-mono text-slate-600 focus:outline-none" />
          <button onClick={() => { navigator.clipboard?.writeText(widgetEmbed); toast.success("Code copié"); }} data-testid="copy-widget-embed"
            className="p-2.5 rounded-xl border border-slate-200 hover:border-sky-300"><Copy className="w-4 h-4 text-sky-600" /></button>
        </div>
        <a href={`/widget/avis/${user?.company_id}`} target="_blank" rel="noreferrer" className="text-sm text-sky-600 hover:underline mt-2 inline-block">Prévisualiser →</a>
      </div>

      {/* Invites */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="invites-panel">
        <h3 className="font-display font-semibold text-navy text-lg">Liens d'invitation</h3>
        <p className="text-sm text-slate-500 mt-1">Envoyez un lien à un client : son avis sera marqué « vérifié ».</p>
        {lastLink && (
          <div className="mt-3 flex items-center gap-2 p-3 rounded-xl bg-sky-50 border border-sky-200" data-testid="last-link">
            <input readOnly value={lastLink} className="flex-1 bg-transparent text-sm text-navy outline-none" />
            <button onClick={() => { navigator.clipboard?.writeText(lastLink); toast.success("Copié"); }} className="p-1.5 rounded-lg hover:bg-white"><Copy className="w-4 h-4 text-sky-600" /></button>
          </div>
        )}
        <div className="mt-4 divide-y divide-slate-100">
          {invites.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between py-3" data-testid={`invite-${inv.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-navy">{inv.client_name || "Client"}{inv.client_email ? ` · ${inv.client_email}` : ""}</div>
                <div className="text-xs text-slate-400 font-mono truncate">/avis/{inv.token}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {inv.used ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold"><BadgeCheck className="w-4 h-4" /> Avis reçu</span>
                  : <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">En attente</span>}
                {!inv.used && <button onClick={() => copy(inv.token)} data-testid={`copy-invite-${inv.id}`} className="p-1.5 rounded-lg hover:bg-slate-100"><Copy className="w-4 h-4 text-slate-500" /></button>}
              </div>
            </div>
          ))}
          {invites.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Aucune invitation envoyée.</p>}
        </div>
      </div>

      {/* Reviews list with reply */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="reviews-list">
        <h3 className="font-display font-semibold text-navy text-lg">Derniers avis</h3>
        <div className="mt-3 divide-y divide-slate-100">
          {(data?.reviews || []).map((r) => (
            <div key={r.id} className="py-4" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-navy">{r.client_name}</span>
                <div className="flex items-center gap-2">
                  <Stars value={r.global} size="w-4 h-4" /><span className="text-sm font-semibold text-navy">{r.global}</span>
                  {r.reported
                    ? <span className="text-[11px] text-amber-600 font-semibold inline-flex items-center gap-0.5"><Flag className="w-3.5 h-3.5" /> Signalé</span>
                    : <button onClick={() => reportReview(r.id)} data-testid={`report-btn-${r.id}`} title="Signaler cet avis" className="p-1 rounded text-slate-400 hover:text-amber-600 hover:bg-amber-50"><Flag className="w-4 h-4" /></button>}
                </div>
              </div>
              {r.comment && <p className="text-sm text-slate-600 mt-1">{r.comment}</p>}
              {r.reply ? (
                <div className="mt-2 ml-4 pl-3 border-l-2 border-sky-200 bg-sky-50/50 rounded-r-lg p-2" data-testid={`reply-${r.id}`}>
                  <div className="text-xs font-semibold text-sky-700 inline-flex items-center gap-1"><Reply className="w-3.5 h-3.5" /> Votre réponse</div>
                  <p className="text-sm text-slate-600 mt-0.5">{r.reply}</p>
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-2">
                  <input value={replyDrafts[r.id] || ""} onChange={(e) => setReplyDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                    placeholder="Répondre publiquement…" data-testid={`reply-input-${r.id}`}
                    className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
                  <button onClick={() => sendReply(r.id)} data-testid={`reply-btn-${r.id}`}
                    className="px-3 py-2 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy-deep inline-flex items-center gap-1"><Reply className="w-4 h-4" /> Répondre</button>
                </div>
              )}
            </div>
          ))}
          {(!data?.reviews || data.reviews.length === 0) && <p className="text-sm text-slate-400 py-6 text-center">Pas encore d'avis. Invitez vos clients !</p>}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="invite-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-display font-bold text-navy">Inviter un client</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <label className="text-xs font-medium text-slate-500">Client existant (optionnel)</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="invite-client-select"
              className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-sky-400">
              <option value="">— Saisir un nom —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {!clientId && (
              <>
                <label className="text-xs font-medium text-slate-500 mt-3 block">Nom du client</label>
                <input value={clientName} onChange={(e) => setClientName(e.target.value)} data-testid="invite-client-name" placeholder="Ex: M. Dupont"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </>
            )}
            <label className="text-xs font-medium text-slate-500 mt-3 block">Email du client (pour envoi automatique)</label>
            <input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} data-testid="invite-client-email" placeholder="client@email.fr"
              className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
            <p className="text-[11px] text-slate-400 mt-1">Si renseigné, le lien d'avis est envoyé par email. Sinon, copiez le lien généré.</p>
            <button onClick={createInvite} data-testid="create-invite-btn"
              className="mt-5 w-full py-3 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep transition-colors inline-flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Générer / envoyer le lien
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
